import React, { useEffect, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  Environment,
  Liveness as LivenessComponent,
  livenessErrors,
} from '@interco/inter-frontend-liveness-lib'
import { ApplicationState } from '@store/types'
import { postValidateBiometrics, setError } from '@store/biometria/actions'
import { showError } from '@store/ui/error/actions'
import { BridgeService } from '@services/bridge'
import { interWbSession, WbEnvironments, WbPermissions } from '@interco/inter-webview-bridge'
import { getLivenessErrorSheet } from '@utils/livenessErrorMessage'
import { shouldFallbackToEnvSerasaSdk } from '@utils/serasaSdkFallback'
import routes from '@routes/routes'

import { Container } from './styles'

const Liveness = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { accessToken } = useSelector((state: ApplicationState) => state.session)
  const { numeroProposta } = useSelector(
    (state: ApplicationState) => state.refinanciamento.proposta,
  )
  const { contratosSelecionados } = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos,
  )
  const proposalCode = String(numeroProposta ?? contratosSelecionados?.[0]?.numeroProposta ?? '')
  const [isPageLoading, setIsPageLoading] = useState(() => {
    if (typeof document === 'undefined') return false
    return document.readyState !== 'complete'
  })
  const [useEnvSdkUrl, setUseEnvSdkUrl] = useState(false)

  useEffect(() => {
    if (BridgeService.isBrowser()) return

    const callMobilePermissions = async () => {
      await interWbSession.requestPermissions(WbPermissions.CAMERA)
      await interWbSession.requestPermissions(WbPermissions.AUDIO)
      await interWbSession.requestPermissions(WbPermissions.STORAGE)
    }

    callMobilePermissions()
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (document.readyState === 'complete') {
      setIsPageLoading(false)
      return undefined
    }

    const handleLoad = () => setIsPageLoading(false)
    window.addEventListener('load', handleLoad, { once: true })

    return () => {
      window.removeEventListener('load', handleLoad)
    }
  }, [])

  const shouldHideLoading =
    BridgeService.getEnvironment() === WbEnvironments.IOS && isPageLoading === true

  return (
    <Container>
      <LivenessComponent
        key={useEnvSdkUrl ? 'env-sdk' : 'bff-sdk'}
        hideLoading={shouldHideLoading}
        accessToken={BridgeService.isBrowser() ? accessToken || '' : ''}
        containerId="liveness-container"
        dataTestId="liveness-container"
        origin={BridgeService.isBrowser() ? 'REFINANCING_WEB' : 'REFINANCING_APP'}
        proposalCode={proposalCode}
        useEnvSdkUrl={useEnvSdkUrl}
        callSuccessAction={() => {
          dispatch(
            postValidateBiometrics({
              callback: () => {
                navigate(routes.REVISAO)
              },
            }),
          )
        }}
        callErrorAction={(error) => {
          if (shouldFallbackToEnvSerasaSdk(error, useEnvSdkUrl)) {
            setUseEnvSdkUrl(true)
            return
          }

          dispatch(setError(true))
          const { title, message } = getLivenessErrorSheet(error?.code)
          dispatch(
            showError({
              title,
              message,
              block: true,
              functionCallback:
                error?.code !==
                livenessErrors.SERASA_EXPERIAN_IDF_LIVENESS_CHECK_FAILED_AND_CANNOT_RETRY_ERROR.code
                  ? () => window.location.reload()
                  : undefined,
            }),
          )
        }}
        environment={process.env.REACT_APP_ENVIROMENT as Environment}
      />
    </Container>
  )
}

export default Liveness
